import React, { useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import { ExportFormat, ExportDataType } from '../types/export.types'
import { downloadExport } from '../utils/fileExport'

interface ExportModalProps {
  open: boolean
  onClose: () => void
  dataType: ExportDataType
  title?: string
}

export const ExportModal: React.FC<ExportModalProps> = ({
  open,
  onClose,
  dataType,
  title,
}) => {
  const [format, setFormat] = useState<ExportFormat>('csv')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleClose = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setError(null)
    setSuccess(false)
    onClose()
  }

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const handleExport = async () => {
    setLoading(true)
    setError(null)
    setSuccess(false)
    try {
      await downloadExport(dataType, format)
      setSuccess(true)
      timerRef.current = setTimeout(handleClose, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setLoading(false)
    }
  }

  const dialogTitle = title ?? (dataType === 'collection' ? 'Export Collection' : 'Export Sessions')

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Typography variant="body2" color="text.secondary">
            Choose a format to export your data.
          </Typography>
          <ToggleButtonGroup
            value={format}
            exclusive
            onChange={(_e, val) => val && setFormat(val)}
            fullWidth
          >
            <ToggleButton value="csv">CSV</ToggleButton>
            <ToggleButton value="json">JSON</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {format === 'csv'
              ? 'Compatible with Excel, Google Sheets, and other spreadsheet apps.'
              : 'Preserves all data relationships. Suitable for re-import or custom processing.'}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">Export downloaded successfully!</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : <DownloadIcon />}
        >
          {loading ? 'Exporting…' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ExportModal
